import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Tooltip } from 'antd';
import styled from 'styled-components';

export interface IInvoiceNumberCell {
  number: string | null;
  isProforma: boolean;
}

/**
 * ТЗ 0101: номер документа в «Moje faktury». Открытая проформа идёт в списке
 * наравне с фактурами VAT, поэтому её помечаем — иначе студент не поймёт,
 * почему потом придёт ещё одна фактура на ту же сумму. Подсказка объясняет:
 * VAT выставят после оплаты.
 */
export const InvoiceNumberCell = memo(({ number, isProforma }: IInvoiceNumberCell) => {
  const { t } = useTranslation();

  return (
    <Cell>
      <DocNumber>{number ?? ''}</DocNumber>
      {isProforma ? (
        <Tooltip title={t('invoices.proformaHint')}>
          <ProformaTag color="purple" data-testid="invoice-proforma-tag" tabIndex={0}>
            {t('invoices.proforma')}
          </ProformaTag>
        </Tooltip>
      ) : null}
    </Cell>
  );
});

/**
 * Таблица «Moje faktury» идёт со `scroll x: max-content`: ширина колонки равна
 * max-content ячейки (flex-wrap тут не спасает — max-content не переносит).
 * Пометка в строку с номером раздувала первую колонку, и на телефоне/планшете
 * срок оплаты с кнопкой «Zapłać» уезжали во внутреннюю прокрутку. Уже
 * `small_desktop` (1110 px) — столбиком: колонка = max(номер, пометка). На
 * десктопе места хватает, там как было — в строку.
 */
const narrow = '@media (max-width: 1109px)';

const Cell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;

  ${narrow} {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 4px;
  }
`;

/** Номер документа не переносится сам по себе (у «FV 12/2026» есть пробел). */
const DocNumber = styled.span`
  white-space: nowrap;
`;

const ProformaTag = styled(Tag)`
  ${narrow} {
    && {
      max-width: 100%;
      margin-inline-end: 0;
    }
  }
`;
